import Box from '@mui/material/Box';
import { type ReactNode } from 'react';

/**
 * Text for screen readers only (§50) — used to say things the layout makes
 * obvious visually but a screen reader would otherwise miss, such as a link
 * opening in a new tab.
 *
 * The sizes are strings on purpose. In MUI's `sx` the sizing properties read a
 * bare number of 1 or less as a *fraction*, so `width: 1` is `width: 100%`,
 * not one pixel — and an absolutely positioned span at 100% of whatever
 * happens to be its containing block is a box the width of the viewport. It is
 * invisible, because `clip-path` hides it, but `clip-path` does not take it
 * out of the layout: it still counts towards scroll overflow. Four of these on
 * the home page's stat band gave the page a 1,060px horizontal scrollbar.
 *
 * `live` turns it into an announcement rather than a label. The two belong in
 * one component because an announcement has to be hidden the same careful way
 * — a live region with a visible height is a blank strip in the layout — and
 * because the live region must be the element whose text changes. `polite`
 * waits for a pause in whatever the screen reader is already saying;
 * `assertive` interrupts, and almost nothing on this site has earned that.
 */
export function VisuallyHidden({
  children,
  live,
  id,
}: {
  children: ReactNode;
  live?: 'polite' | 'assertive';
  /** Lets a visible control point at these words with `aria-describedby`. */
  id?: string;
}) {
  return (
    <Box
      component="span"
      {...(live ? { 'aria-live': live } : {})}
      {...(id ? { id } : {})}
      sx={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}
